import { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, X, Search } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage } from '@/lib/api';

// PR-RIYADH-FIX-2 MAY 12 — typeahead city picker with inline "Add new city".
// Sister to ClientPickerOrCreate. Returns city.id (UUID) AND the chosen
// name_en so the legacy flatbed_trips.pickup_city TEXT column still gets
// populated for back-compat.

export interface CityOption {
  id: string;
  code: string | null;
  nameEn: string;
  nameAr: string | null;
  region: string | null;
  latitude: number | null;
  longitude: number | null;
}

interface Props {
  value: string | null;                     // city id
  onChange: (id: string | null, city: CityOption | null) => void;
  placeholder?: string;
  className?: string;
}

interface RawCity {
  id: string;
  code: string | null;
  name_en: string;
  name_ar: string | null;
  region: string | null;
  latitude: string | null;
  longitude: string | null;
  is_active?: boolean;
}

function normalize(rows: RawCity[]): CityOption[] {
  return rows.map((c) => ({
    id: c.id,
    code: c.code,
    nameEn: c.name_en,
    nameAr: c.name_ar,
    region: c.region,
    latitude: c.latitude != null ? Number(c.latitude) : null,
    longitude: c.longitude != null ? Number(c.longitude) : null,
  }));
}

export function CityPickerOrCreate({ value, onChange, placeholder = 'Pick or add city…', className }: Props) {
  const [items, setItems] = useState<CityOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const [newNameEn, setNewNameEn] = useState('');
  const [newNameAr, setNewNameAr] = useState('');
  const [newRegion, setNewRegion] = useState<'central' | 'eastern' | 'western' | 'northern' | 'southern' | ''>('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const r = await apiClient.get<{ items: RawCity[] }>('/api/v1/cities?activeOnly=true');
      setItems(normalize(r.data.items ?? []));
    } catch { setItems([]); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((c) =>
      c.nameEn.toLowerCase().includes(q)
      || (c.nameAr ?? '').toLowerCase().includes(q)
      || (c.code ?? '').toLowerCase().includes(q));
  }, [items, query]);

  const selected = items.find((c) => c.id === value) ?? null;

  async function submitCreate() {
    if (!newNameEn.trim()) { setError('Name required'); return; }
    setError(null);
    setCreating(true);
    try {
      const r = await apiClient.post<{ city: RawCity }>('/api/v1/cities', {
        nameEn: newNameEn.trim(),
        nameAr: newNameAr.trim() || undefined,
        region: newRegion || undefined,
      });
      const c = r.data.city;
      const opt: CityOption = {
        id: c.id,
        code: c.code,
        nameEn: c.name_en,
        nameAr: c.name_ar,
        region: c.region,
        latitude: c.latitude != null ? Number(c.latitude) : null,
        longitude: c.longitude != null ? Number(c.longitude) : null,
      };
      setItems((prev) => {
        const without = prev.filter((x) => x.id !== opt.id);
        return [...without, opt].sort((a, b) => a.nameEn.localeCompare(b.nameEn));
      });
      onChange(opt.id, opt);
      setShowCreate(false); setOpen(false);
      setNewNameEn(''); setNewNameAr(''); setNewRegion('');
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className={'relative ' + (className ?? '')}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-11 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-base text-left"
      >
        <span className={selected ? '' : 'text-muted-foreground'}>
          {selected ? (selected.nameEn + (selected.code ? ` · ${selected.code}` : '')) : placeholder}
        </span>
        <span className="text-muted-foreground text-xs">▾</span>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-md border border-border bg-card shadow-lg max-h-[380px] flex flex-col">
          <div className="p-2 border-b border-border flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to filter…"
              className="flex-1 bg-transparent outline-none text-sm"
            />
            {value && (
              <button type="button" onClick={() => { onChange(null, null); setOpen(false); }} className="text-xs text-muted-foreground hover:text-foreground">
                Clear
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-3 text-sm text-muted-foreground inline-flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-3 text-sm text-muted-foreground">No cities match.</div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => { onChange(c.id, c); setOpen(false); setQuery(''); }}
                  className={'w-full text-left px-3 py-2 hover:bg-muted/40 text-sm flex items-center justify-between '
                    + (c.id === value ? 'bg-muted/30' : '')}
                >
                  <span>{c.nameEn}{c.nameAr ? ` · ${c.nameAr}` : ''}</span>
                  {c.code && <span className="text-xs text-muted-foreground font-mono">{c.code}</span>}
                </button>
              ))
            )}
          </div>

          {!showCreate ? (
            <div className="p-2 border-t border-border">
              <Button type="button" variant="outline" size="sm" className="w-full gap-2" onClick={() => { setShowCreate(true); setNewNameEn(query); }}>
                <Plus className="h-3.5 w-3.5" /> Add new city{query ? ` "${query}"` : ''}
              </Button>
            </div>
          ) : (
            <div className="p-3 border-t border-border space-y-2 bg-muted/10">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">New city</div>
                <button type="button" onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="h-3 w-3" />
                </button>
              </div>
              <div className="space-y-2">
                <Field label="English name (required)">
                  <Input value={newNameEn} onChange={(e) => setNewNameEn(e.target.value)} placeholder="e.g. Al Hada" />
                </Field>
                <Field label="Arabic name (optional)">
                  <Input dir="rtl" value={newNameAr} onChange={(e) => setNewNameAr(e.target.value)} placeholder="الهدا" />
                </Field>
                <Field label="Region">
                  <select
                    value={newRegion}
                    onChange={(e) => setNewRegion(e.target.value as typeof newRegion)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="">—</option>
                    <option value="central">Central</option>
                    <option value="eastern">Eastern</option>
                    <option value="western">Western</option>
                    <option value="northern">Northern</option>
                    <option value="southern">Southern</option>
                  </select>
                </Field>
                {error && <div className="text-xs text-rose-600 dark:text-rose-400">{error}</div>}
                <div className="flex gap-2">
                  <Button type="button" size="sm" onClick={submitCreate} disabled={creating || !newNameEn.trim()} className="gap-2">
                    {creating && <Loader2 className="h-3 w-3 animate-spin" />} Save & use
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
